/**
 * Tuile de KPI.
 *
 * Un chiffre isolé n'est pas un graphique : le tracer en une barre unique
 * gaspille de la place et ne dit rien de plus. Libellé, valeur, variation
 * optionnelle, niveau optionnel.
 *
 * ── Le « comment c'est calculé » tient dans un (i) ─────────────────────────
 * La précision (`hint`) ne s'étale plus sous la valeur : elle doublait la
 * hauteur des tuiles. Un bouton (i) à côté du libellé l'ouvre en infobulle au
 * survol, au focus clavier, ou au clic (tactile). Le texte reste dans la page
 * pour les lecteurs d'écran (`aria-describedby`) et pour la recherche.
 *
 * La valeur reste en couleur de texte, jamais en couleur de série : le rose
 * de marque plafonne à 4,0:1 sur la surface, ce qui exclut tout texte de
 * taille normale.
 *
 * ── Le niveau teinte la carte, il ne la repeint pas ─────────────────────────
 * Avec un `status`, la carte prend un voile de 12 % et une bordure à la couleur
 * du niveau. Elle se lit « rouge » au premier coup d'œil sans que la luminance
 * du fond bouge assez pour entamer le contraste du texte posé dessus. Un aplat
 * saturé aurait obligé à repeindre tout le texte, et le grand nombre d'une
 * tuile n'a aucune couleur de remplacement qui tienne le contraste.
 *
 * Et le niveau ne repose JAMAIS sur la seule couleur : il vient avec l'icône et
 * le libellé de `AlertList` — mêmes trois niveaux, mêmes glyphes, mêmes
 * teintes. Un daltonien, une impression noir et blanc ou un mode contrastes
 * forcés doivent rester lisibles.
 */

import type { StatusLevel } from '@shared/types/stats';
import { useId, useState } from 'react';

import { IMPACT_TONE_COLORS, STATUS_COLORS, STATUS_TINTS } from '../lib/theme';
import { impactTone, verdictGlyph, verdictLabel, type ImpactVerdict } from '../lib/verdict';
import { Card } from './Card';

/** Glyphes et libellés repris de `AlertList` : un seul vocabulaire de niveau. */
const STATUS_ICONS: Record<StatusLevel, string> = {
  good: '✓',
  warning: '⚠',
  critical: '✕',
};

export interface StatDelta {
  /** Écart déjà mis en forme, ex. « +5,7 % vs période préc. ». */
  text: string;
  verdict: ImpactVerdict;
  /**
   * La métrique est-elle meilleure quand elle monte ?
   *
   * Explicite et sans valeur par défaut : c'est la seule chose qui transforme
   * une direction en jugement. La laisser implicite ferait afficher du vert sur
   * la hausse d'un taux de churn. `null` : ni l'un ni l'autre (une dépense),
   * la ligne reste de couleur neutre.
   */
  higherIsBetter: boolean | null;
  /**
   * Second chiffre, en retrait, dans la couleur de texte neutre.
   *
   * Sert à porter l'écart en POINTS à côté de la variation relative : « +5,7 % »
   * tout seul se fait lire comme 5,7 points par la moitié des lecteurs, et les
   * deux chiffres sont justes. Le relatif mène parce qu'il rend deux périodes
   * comparables ; le point précise de combien le taux a bougé.
   */
  note?: string;
}

export interface StatStatus {
  level: StatusLevel;
  /** Ce que le niveau signifie, en clair. Double la couleur, jamais l'inverse. */
  label: string;
}

interface StatTileProps {
  label: string;
  value: string;
  hint?: string;
  /**
   * Variations à annoncer sous la valeur, une par ligne.
   *
   * Un tableau et non un champ unique : la tuile de pénétration en porte deux
   * (période/période et année/année), et deux tuiles jumelles auraient divergé.
   * Omis, la tuile est un simple chiffre.
   */
  deltas?: readonly StatDelta[];
  /** Niveau au regard d'un seuil de métier. Omis, la tuile reste neutre. */
  status?: StatStatus;
}

export function StatTile({ label, value, hint, deltas, status }: StatTileProps) {
  const hintId = useId();
  const [open, setOpen] = useState(false);
  return (
    <Card
      style={
        status
          ? {
              background: STATUS_TINTS[status.level],
              borderColor: STATUS_COLORS[status.level],
            }
          : undefined
      }
    >
      <p className="kpi__label">
        {label}
        {hint && (
          <span className={open ? 'kpi__info is-open' : 'kpi__info'}>
            <button
              type="button"
              className="kpi__info-button"
              aria-label="Comment c'est calculé"
              aria-describedby={hintId}
              aria-expanded={open}
              onClick={() => setOpen((current) => !current)}
              onBlur={() => setOpen(false)}
            >
              i
            </button>
            <span id={hintId} role="tooltip" className="kpi__hint kpi__info-bubble">
              {hint}
            </span>
          </span>
        )}
      </p>

      {/* Le grand nombre prend la couleur du niveau : c'est LUI qu'on vient
          lire, et la bordure seule se remarque moins qu'on ne le croit. Les
          teintes de STATUS_COLORS sont calibrees pour le fond sombre
          (4,77:1 au minimum), donc utilisables en texte. */}
      <p className="kpi__value" style={status ? { color: STATUS_COLORS[status.level] } : undefined}>
        {value}
      </p>

      {status && (
        <p className="kpi__status" style={{ color: STATUS_COLORS[status.level] }}>
          <span aria-hidden="true">{STATUS_ICONS[status.level]}</span> {status.label}
        </p>
      )}

      {deltas?.map((delta) => {
        // « +9,4 % vs période préc. » : la variation part en pastille, le
        // « vs … » reste en texte discret. Sans référence (« aucune charge l'an
        // dernier »), pas de pastille : la ligne entière est discrète.
        const at = delta.text.indexOf(' vs ');
        const plain = delta.verdict === 'insufficient' || at < 0;
        const change = plain ? null : delta.text.slice(0, at);
        const against = plain ? delta.text : delta.text.slice(at + 1);
        return (
          <p
            key={delta.text}
            className="kpi__delta"
            style={{ color: IMPACT_TONE_COLORS[impactTone(delta.verdict, delta.higherIsBetter)] }}
          >
            {/* Fleche ET texte : la couleur ne porte jamais seule le sens. Le
                libelle en clair part au lecteur d'ecran, que « ▲ » laisserait
                sans information. */}
            {change !== null ? (
              <span className="kpi__delta-pill">
                <span aria-hidden="true">{verdictGlyph(delta.verdict)}</span> {change}
              </span>
            ) : (
              <span aria-hidden="true">{verdictGlyph(delta.verdict)}</span>
            )}
            {against && <span className="kpi__delta-label">{against}</span>}
            {/* En retrait et en couleur neutre : c'est une precision de l'ecart,
                pas un second verdict. AVANT le libelle pour lecteur d'ecran, pour
                qu'un copier-coller de la tuile reste lisible — le texte en
                `sr-only` est invisible mais reste selectionnable. */}
            {delta.note && <span className="kpi__delta-note">{delta.note}</span>}
            <span className="sr-only"> — {verdictLabel(delta.verdict)}</span>
          </p>
        );
      })}
    </Card>
  );
}
